import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiError, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, ChevronRight } from "lucide-react";

export default function Impianti() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const customerFilter = params.get("customer");
  const load = () =>
    api.get("/plants", { params: customerFilter ? { customer_id: customerFilter } : {} })
      .then(({ data }) => setRows(data));

  useEffect(() => {
    load();
    if (user?.role === "azienda") api.get("/customers").then(({ data }) => setCustomers(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerFilter]);

  const fields = [
    {
      name: "customer_id", label: "Cliente", type: "select", required: true, full: true,
      options: customers.map((c) => ({ value: c.id, label: c.name })),
    },
    { name: "name", label: "Nome impianto", required: true, full: true },
    { name: "code", label: "Codice (vuoto = automatico)" },
    {
      name: "type", label: "Tipologia", type: "select",
      options: [
        { value: "estintori", label: "Estintori" },
        { value: "idranti", label: "Idranti / naspi" },
        { value: "rivelazione", label: "Rivelazione incendi" },
        { value: "sprinkler", label: "Sprinkler" },
        { value: "evacuazione", label: "Evacuazione / EVAC" },
      ],
    },
    { name: "address", label: "Indirizzo", full: true },
    { name: "city", label: "Città" },
    { name: "installed_at", label: "Data installazione", type: "date" },
    { name: "lat", label: "Latitudine", type: "number" },
    { name: "lng", label: "Longitudine", type: "number" },
    { name: "notes", label: "Note", type: "textarea", full: true },
  ];

  const save = async (values) => {
    try {
      if (editing) await api.put(`/plants/${editing.id}`, values);
      else await api.post("/plants", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="impianti-page">
      <PageHeader title="Anagrafica impianti" subtitle={`${rows.length} impianti`}>
        {user?.role === "azienda" && (
          <Button className="h-11 rounded-sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-plant-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuovo impianto
          </Button>
        )}
      </PageHeader>

      <ListCard testid="impianti-list">
        {rows.map((p) => (
          <div key={p.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold">{p.name}</p>
                <Badge variant="secondary" className="rounded-sm capitalize">{p.type}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {p.customer_name} · {p.city || "—"} · installato il {itDate(p.installed_at)}
              </p>
            </div>
            <div className="flex gap-2">
              {user?.role === "azienda" && (
                <Button variant="outline" size="sm" className="rounded-sm"
                        onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-plant-${p.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button asChild size="sm" className="rounded-sm">
                <Link to={`/impianti/${p.id}`} data-testid={`open-plant-${p.id}`}>
                  Dettaglio <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty text="Nessun impianto registrato." />}
      </ListCard>

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Modifica impianto" : "Nuovo impianto"}
                  fields={fields}
                  initial={editing || { type: "estintori", ...(customerFilter ? { customer_id: customerFilter } : {}) }}
                  onSubmit={save} testid="plant-dialog" />
    </div>
  );
}
